import { rewriteSearchQuery } from '@theme/search-query-rewriter';
import { resolveBrandCollectionUrl } from '@theme/brand-collection-url';

/**
 * 인앱 헤더(type: search)의 인라인 검색 폼.
 *
 * - 좌측 검색 아이콘(submit) 또는 Enter → 실제 검색 실행
 * - 인풋 → 직접 입력
 * - 우측 X(클리어) → 입력값 초기화 (노출 여부는 CSS `:has(:not(:placeholder-shown))`가 처리)
 *
 * 검색어는 검색 페이지와 동일하게 rewriteSearchQuery 로 재작성해 이동한다.
 * 입력값이 비어 있으면 이동하지 않는다(검색 페이지 규칙: q 빈 값 리다이렉트 금지).
 */
class InAppHeaderSearchComponent extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-in-app-search-input]');
    this.form = this.input?.form ?? this.querySelector('form');
    this.clearButton = this.querySelector('[data-in-app-search-clear]');

    this.#applyUrlKeyword();

    this.form?.addEventListener('submit', this.handleSubmit);
    this.clearButton?.addEventListener('click', this.handleClear);
  }

  /**
   * URL 검색어로 입력창 기본값을 채운다.
   * `q`는 서버(search-display-terms)에서 이미 채워지므로, 값이 비어 있을 때만
   * 브랜드 검색 페이지(`?search-text=...`)의 search-text 파라미터로 폴백한다.
   * (_search-input.liquid 과 동일 동작)
   */
  #applyUrlKeyword() {
    if (!this.input || this.input.value) return;
    const searchText = new URLSearchParams(window.location.search).get('search-text');
    if (searchText) this.input.value = searchText;
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.handleSubmit);
    this.clearButton?.removeEventListener('click', this.handleClear);
  }

  handleSubmit = (event) => {
    event.preventDefault();
    if (!this.form || !this.input) return;

    const rawKeyword = this.input.value || '';
    if (!rawKeyword.trim()) {
      this.input.focus();
      return;
    }

    const params = new URLSearchParams(new FormData(this.form));
    params.set('q', rewriteSearchQuery(rawKeyword));

    if (window.GBTrack) {
      window.GBTrack.track('search_input_submit', {
        tracking_id: this.dataset.trackingId || '',
        keyword: rawKeyword,
      });
    }

    // 브랜드명을 검색한 경우 /search 를 거치지 않고 브랜드 컬렉션으로 바로 보낸다.
    const brandCollectionUrl = resolveBrandCollectionUrl(rawKeyword);
    if (brandCollectionUrl) {
      window.location.assign(brandCollectionUrl);
      return;
    }

    window.location.assign(`${this.form.action}?${params.toString()}`);
  };

  handleClear = (event) => {
    event.preventDefault();
    if (!this.input) return;

    if (window.GBTrack) {
      window.GBTrack.track('search_input_clear', {
        tracking_id: this.dataset.trackingId || '',
        keyword: this.input.value || '',
      });
    }

    this.input.value = '';
    this.input.focus();
  };
}

if (!customElements.get('in-app-header-search-component')) {
  customElements.define('in-app-header-search-component', InAppHeaderSearchComponent);
}
